import type { PosDatabase } from '@core/db/db'
import type { EventBus } from '@core/bus/EventBus'
import { PosEvent, type SaleContext, type SaleItem } from '@core/bus/events'
import type { MarkingIssue } from '@core/bus/types'
import type {
  FiscalReceiptData,
  QueuedSale,
  ReceiptStatus,
  SaleReceipt,
  SaleReceiptItem,
  SettlementType
} from '@core/db/models'
import { fiscalService } from '@core/services/fiscal/FiscalService'
import { isNativeKkt } from '@core/native/kkt'
import { generateUUID } from '@core/utils/crypto'
import type { MarkingProvider } from '@core/marking/types'
import { getActiveShiftContext } from '@core/services/shift/activeShiftContext'
import { returnableQtyByLine, validateReturnItems } from './returnValidation'
import { isReturnSourceAllowed, resolveReturnFiscalIntent } from './returnFiscalIntent'
import { StockService } from '@core/services/stock/StockService'
import type { Writeoff, WriteoffReasonCode } from '@core/db/models'

/**
 * 65.14: через сколько считать незавершённую операцию «зависшей» и разрешить
 * повтор. ККТ отвечает за 10 секунд, поэтому 2 минуты — безопасный запас.
 */
export const MARKING_OPERATION_STALE_MS = 2 * 60 * 1000

/** Входная позиция чека для оформления продажи. */
export interface CheckoutItem {
  /**
   * Стабильный id строки корзины/чека (ARCH-TASK-65.10). Для возврата
   * маркированного товара нужен line-level выбор: конкретная строка и код.
   */
  lineId?: string
  /**
   * Строка исходного чека, которую возвращаем (line-level, 65.10).
   * Заполняется только в режиме возврата.
   */
  originalLineId?: string
  barcode: string
  gtin?: string
  name: string
  qty: number
  price: number
  /** Единица измерения ('шт' | 'кг'): нужна для корректного возврата (65). */
  unit?: string
  /** Код маркировки DataMatrix (если уже отсканирован кассиром). */
  datamatrix?: string
  /** Признак, что позиция подлежит маркировке (эвристика молочки). */
  requiresMarking?: boolean
  /** Кассир осознанно продал позицию без кода (право `can_skip_marking`). */
  markingSkipped?: boolean
  /** Код позиции не прошёл проверку ЧЗ (65.12). */
  markingInvalid?: boolean
  /** Причина пропуска/невалидного кода. */
  markingReason?: string
  /** Статус проверки кода позиции в ЧЗ (65.12). */
  czStatus?: 'valid' | 'invalid' | 'unknown'
}

/** Параметры оформления продажи. */
export interface CheckoutInput {
  items: CheckoutItem[]
  /** Способ оплаты: 'cash' | 'card' | 'sbp'. */
  paymentMethod?: string
  /**
   * Признак расчёта (ARCH-TASK-65): 'income' (по умолчанию) — продажа,
   * 'income_return' — возврат. Сумма возврата хранится отрицательной.
   */
  settlementType?: SettlementType
  /**
   * Исходный чек возврата (ARCH-TASK-65.5): business-id продажи. Задаётся при
   * возврате по чеку; без него возврат считается «без чека» (ручной) и лимит
   * по проданному не проверяется.
   */
  originalReceiptId?: string
  /**
   * «Возврат + списание» (65.10): вернуть товар на склад и сразу списать.
   * Создаётся документ Writeoff и два движения ledger (итог 0).
   */
  writeoff?: {
    reasonCode: WriteoffReasonCode
    comment?: string
  }
}

/** Исход оформления продажи. */
export type CheckoutResult =
  | {
      ok: true
      receipt: SaleReceipt
      /**
       * Предупреждение для UI (напр. «ККТ недоступна — чек будет фискализирован
       * позже»). Продажа при этом успешна.
       */
      warning?: string
    }
  | {
      ok: false
      /**
       * 'validation' — возврат не проходит сверку с исходным чеком (65.5):
       * позиции/количество больше проданного либо чек уже возвращён.
       */
      stage: 'pre_sale' | 'payment' | 'validation'
      reason: string
      receiptId: string
      /** Проблемы маркировки для UI (65.10): убрать позицию / пропустить. */
      markingIssues?: MarkingIssue[]
    }

/** Задача замены non_fiscal → fiscal (B3/65.14). */
export interface ReplacementTask {
  receipt: SaleReceipt
  /** Сколько миллисекунд чек ждёт решения (для «стареющих» уведомлений). */
  ageMs: number
}

/** Подтверждение кассира перед возвратом и повторной оплатой. */
export interface PrintReplacementOptions {
  /** Сумма, которую кассир подтвердил в модалке (должна совпасть с чеком). */
  confirmedAmount: number
  cashierName?: string
  /** Откуда пришло решение: касса или облако (9c). */
  decisionSource?: 'pos' | 'cloud'
  /** Кто принял решение в облаке. */
  decidedBy?: string
  decidedAt?: number
  decisionComment?: string
}

/** Параметры отмены hold-чека (локально или по решению облака). */
export interface CancelHeldOptions {
  cancelMode?: 'refund' | 'writeoff'
  writeoffReason?: WriteoffReasonCode
  decisionSource?: 'pos' | 'cloud'
  decidedBy?: string
  decidedAt?: number
  decisionComment?: string
}

/** Итог печати замены. */
export type PrintReplacementResult =
  | { ok: true; receipt: SaleReceipt; replaced: SaleReceipt }
  | { ok: false; reason: string; receipt?: SaleReceipt }

/** Опции оформления продажи. */
export interface CheckoutOptions {
  /**
   * Фискальное НАМЕРЕНИЕ кассира (галочка «Фискализировать чек»), по умолчанию
   * true. НЕ зависит от доступности ККТ: при true и недоступной ККТ чек
   * уходит в очередь дофискализации, а не теряется.
   */
  fiscalIntent?: boolean
  /** Имя кассира для фискального чека. */
  cashierName?: string
  /**
   * Право кассира продать маркированный товар без кода (65.10).
   * Без него `markingSkipped`/`markingInvalid` в позициях отклоняются.
   */
  canSkipMarking?: boolean
  /** Право оформить списание (65.10). */
  canWriteoff?: boolean
  /** Право снять фискализацию с немаркированной продажи (65.10). */
  canSkipFiscalization?: boolean
  /**
   * Native Android KKT пока не умеет передавать тег 2000. Если true, любой
   * чек с маркированной позицией блокируется до PAYMENT_REQUESTED.
   * По умолчанию вычисляется по платформе; тесты могут задать явно.
   */
  nativeMarkingBlocked?: boolean
}

/**
 * Оркестратор жизненного цикла продажи.
 *
 * Последовательность:
 * 1. Генерируем бизнес-id чека (UUID).
 * 2. PRE_SALE → плагины (Честный Знак и т.п.) могут наложить veto.
 *    Любой veto прерывает продажу с причиной.
 * 3. PAYMENT_REQUESTED → плагин оплаты (СБП NewPay) инициирует платёж.
 *    Veto на этой стадии = оплата не прошла.
 * 4. Фискализация по НАМЕРЕНИЮ кассира: 'fiscalized' / 'offline_pending' /
 *    'non_fiscal' (см. ReceiptStatus). Недоступность ККТ не блокирует продажу.
 * 5. Чек пишется в Dexie (receipts) с фискальным статусом.
 * 6. offline_pending дублируется в offlineQueue (type='fiscalize') — очередь
 *    дофискализации обрабатывается FiscalQueueWorker.
 * 7. SALE_COMPLETED → пост-обработка (Меркурий гасит ВСД и т.п.).
 *
 * Вся доменная логика живёт здесь, а не в React-компоненте — страница кассы
 * лишь вызывает checkout() и реагирует на результат (в т.ч. показывает модалку
 * ввода марки при veto Честного Знака и повторяет попытку).
 */
export class SaleService {
  private readonly stock: StockService

  constructor(
    private readonly db: PosDatabase,
    private readonly bus: EventBus,
    /**
     * Модуль маркировки юрисдикции (`ru-cz`, в будущем KZ/UZ).
     * `null` — маркировка не регулируется: код сохраняется как есть,
     * структурную проверку делает сам модуль на PRE_SALE.
     */
    private readonly marking: MarkingProvider | null
  ) {
    this.stock = new StockService(db)
  }

  /** Сгенерировать бизнес-id чека (валидный RFC4122 UUIDv4). */
  private newReceiptId(): string {
    return generateUUID()
  }

  /**
   * 65.14: операции с деньгами/ККТ допустимы только в активной смене той же
   * зоны и с тем же номером, где был исходный чек. Иначе фискальный документ
   * окажется вне Z-отчёта, а наличные — вне ящика.
   */
  private async assertActiveShiftFor(
    receipt: Pick<SaleReceipt, 'zoneId' | 'shiftNumber'>
  ): Promise<{ ok: true; zoneId: string; shiftNumber: number } | { ok: false; reason: string }> {
    const context = await this.getActiveShiftContext()
    if (!context.zoneId || context.shiftNumber == null) {
      return { ok: false, reason: 'Нет активной смены — операция с чеком невозможна' }
    }
    if (receipt.zoneId && receipt.zoneId !== context.zoneId) {
      return { ok: false, reason: 'Чек принадлежит другой зоне — операция невозможна' }
    }
    if (receipt.shiftNumber != null && receipt.shiftNumber !== context.shiftNumber) {
      return {
        ok: false,
        reason: 'Смена уже закрыта — операция возможна только в исходной смене'
      }
    }
    return { ok: true, zoneId: context.zoneId, shiftNumber: context.shiftNumber }
  }

  /**
   * 65.14: восстановить операции, прерванные падением вкладки/приложения.
   *
   * Фискальный чек мог быть уже напечатан, поэтому мы НЕ пытаемся печатать
   * повторно автоматически. Возвращаем задачу в `failed` (с сохранённым
   * стабильным id нового чека) — кассир повторит, и агент ККТ отдаст
   * закэшированные реквизиты по тому же uuid вместо второй печати.
   */
  async recoverInterruptedOperations(now = Date.now()): Promise<number> {
    let recovered = 0
    const cutoff = now - MARKING_OPERATION_STALE_MS

    const processing = await this.db.receipts
      .where('replacementState')
      .equals('processing')
      .toArray()
    for (const receipt of processing) {
      if (receipt.id == null) continue
      const startedAt = receipt.replacementStartedAt ?? 0
      if (startedAt > cutoff) continue
      await this.db.receipts.update(receipt.id, {
        replacementState: 'failed',
        replacementError:
          receipt.replacementError ??
          'Замена была прервана. Повторите печать — чек не будет напечатан дважды.',
        replacementStartedAt: undefined
      })
      recovered += 1
    }

    // Hold-печать: тот же принцип. Если код valid — разрешаем повтор с тем же
    // receiptId; если invalid/unknown — оставляем решение за кассиром.
    const held = await this.db.receipts
      .where('status')
      .equals('marking_review')
      .toArray()
    for (const receipt of held) {
      if (receipt.id == null) continue
      const startedAt = receipt.markingPrintStartedAt ?? 0
      if (startedAt === 0 || startedAt > cutoff) continue
      await this.db.receipts.update(receipt.id, {
        markingPrintStartedAt: undefined,
        markingPrintState:
          receipt.czStatus === 'valid'
            ? 'ready'
            : receipt.czStatus === 'invalid'
              ? 'failed'
              : receipt.markingPrintState
      })
      recovered += 1
    }

    if (recovered > 0) {
      console.warn(`[Sale] восстановлено прерванных операций маркировки: ${recovered}`)
    }
    return recovered
  }

  private toSaleItems(items: CheckoutItem[]): SaleItem[] {
    return items.map((i) => ({
      lineId: i.lineId,
      gtin: i.gtin,
      name: i.name,
      qty: i.qty,
      price: i.price,
      datamatrix: i.datamatrix,
      requiresMarking: i.requiresMarking,
      markingSkipped: i.markingSkipped,
      markingInvalid: i.markingInvalid,
      markingReason: i.markingReason,
      czStatus: i.czStatus
    }))
  }

  private toReceiptItems(items: CheckoutItem[], isReturn = false): SaleReceiptItem[] {
    return items.map((i) => ({
      // У возврата lineId — это строка ИСХОДНОГО чека (или undefined для
      // старых чеков: тогда матчинг идёт по DataMatrix/штрихкоду). У продажи —
      // собственная строка корзины.
      lineId: isReturn ? i.originalLineId : i.lineId,
      barcode: i.barcode,
      gtin: i.gtin,
      name: i.name,
      qty: i.qty,
      price: i.price,
      unit: i.unit,
      datamatrix: i.datamatrix,
      requiresMarking: i.requiresMarking,
      markingSkipped: i.markingSkipped,
      markingInvalid: i.markingInvalid,
      markingReason: i.markingReason,
      czStatus: i.czStatus
    }))
  }

  /**
   * Идентификатор устройства (терминала) из meta.
   * Если не задан — генерируем и сохраняем: на смартфоне (ширина < 768px)
   * «phone-xxxxxxxx» (для бейджа «📱 Мобильная касса» в админке), иначе
   * стационарный «pos-terminal-1». Единый формат с SyncService.
   */
  private async getDeviceId(): Promise<string> {
    const entry = await this.db.meta.get('device_id')
    if (typeof entry?.value === 'string' && entry.value) {
      return entry.value
    }
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
    const deviceId = isMobile ? `phone-${generateUUID().slice(0, 8)}` : 'pos-terminal-1'
    await this.db.meta.put({ key: 'device_id', value: deviceId })
    return deviceId
  }

  /**
   * Контекст активной смены (зона + номер) для привязки чека.
   *
   * Нужен для облачного Z-отчёта ПО ЗОНЕ: «Z смены» агрегирует чеки всех
   * устройств рабочего места, поэтому каждый чек несёт zoneId и shiftNumber.
   * Зона берётся из активной смены (meta activeShiftId → db.shifts), с
   * фолбэком на первую открытую смену в локальной БД.
   */
  private async getActiveShiftContext(): Promise<{
    zoneId?: string
    storeId?: string
    shiftNumber?: number
  }> {
    return getActiveShiftContext(this.db)
  }

  /**
   * Поставить чек в очередь синхронизации с облаком (sales_queue).
   * Поля в формате облачного API (quantity/marking_code).
   */
  /** Собрать строку sales_queue без записи (для транзакций/тестов). */
  private buildSyncQueueRow(
    deviceId: string,
    receiptId: string,
    items: CheckoutItem[],
    total: number,
    soldAtIso: string,
    paymentMethod?: string,
    fiscalData?: FiscalReceiptData,
    status?: ReceiptStatus,
    shiftContext?: { zoneId?: string; storeId?: string; shiftNumber?: number; cashierName?: string },
    settlementType: SettlementType = 'income',
    originalReceiptId?: string,
    offlineReceiptNumber?: string
  ): QueuedSale {
    return {
      id: receiptId,
      device_id: deviceId,
      total_amount: total,
      items: items.map((i) => ({
        line_id: i.lineId,
        barcode: i.barcode,
        name: i.name,
        quantity: i.qty,
        price: i.price,
        unit: i.unit,
        marking_code: i.datamatrix
      })),
      sold_at: soldAtIso,
      is_synced: false,
      payment_method: paymentMethod || 'cash',
      // Возврат уходит в облако с отрицательной суммой и признаком расчёта.
      settlement_type: settlementType,
      // Исходный чек возврата (65.5): связь для аудита и лимита возвратов.
      original_receipt_id: originalReceiptId,
      offline_receipt_number: offlineReceiptNumber,
      // 65.10: признаки маркировки — для отчёта «Продажи без кода».
      marking_skipped: items.some((i) => i.markingSkipped),
      marking_invalid: items.some((i) => i.markingInvalid),
      marking_reason: items.find((i) => i.markingReason)?.markingReason,
      status,
      fiscalData,
      // Контекст смены — для облачного Z по зоне.
      zone_id: shiftContext?.zoneId,
      store_id: shiftContext?.storeId,
      shift_number: fiscalData?.shiftNumber ?? shiftContext?.shiftNumber,
      cashier_name: shiftContext?.cashierName
    }
  }

  private async enqueueForSync(
    receiptId: string,
    items: CheckoutItem[],
    total: number,
    soldAtIso: string,
    paymentMethod?: string,
    fiscalData?: FiscalReceiptData,
    status?: ReceiptStatus,
    shiftContext?: { zoneId?: string; storeId?: string; shiftNumber?: number; cashierName?: string },
    settlementType: SettlementType = 'income',
    originalReceiptId?: string,
    offlineReceiptNumber?: string
  ): Promise<void> {
    const deviceId = await this.getDeviceId()
    await this.db.sales_queue.add(
      this.buildSyncQueueRow(
        deviceId,
        receiptId,
        items,
        total,
        soldAtIso,
        paymentMethod,
        fiscalData,
        status,
        shiftContext,
        settlementType,
        originalReceiptId,
        offlineReceiptNumber
      )
    )
  }

  /**
   * Позиции всех уже оформленных возвратов по исходному чеку (65.5).
   * Нужны и сервису (лимит возврата), и UI (остаток по позиции).
   */
  private async returnedItemsOf(originalReceiptId: string): Promise<SaleReceiptItem[]> {
    const returns = await this.db.receipts
      .where('originalReceiptId')
      .equals(originalReceiptId)
      .toArray()
    return returns.flatMap((r) => r.items)
  }

  /**
   * Сколько ещё можно вернуть по чеку: остаток по КАЖДОЙ строке и статусы
   * «частично/полностью возвращён» (ARCH-TASK-65.5). Для UI списка чеков.
   */
  async getReturnedSummary(receipt: SaleReceipt): Promise<{
    fullyReturned: boolean
    partiallyReturned: boolean
    /** Остаток по строкам чека — та же длина и порядок, что receipt.items. */
    byLine: number[]
  }> {
    const returned = await this.returnedItemsOf(receipt.receiptId)
    const byLine = returnableQtyByLine(receipt.items, returned)
    // Пустой чек не может быть «возвращён полностью» — иначе его не выбрать.
    const fullyReturned = receipt.items.length > 0 && byLine.every((left) => left === 0)
    const partiallyReturned = byLine.some(
      (left, idx) => left < (receipt.items[idx]?.qty ?? 0)
    )
    return { fullyReturned, partiallyReturned, byLine }
  }

  /**
   * Поиск чеков этой кассы для возврата (номер / дата / сумма).
   * Учитывает уже оформленные возвраты: UI показывает остаток и блокирует
   * полностью возвращённые чеки (ARCH-TASK-65.5).
   */
  async findReceiptsForReturn(
    query = '',
    limit = 20
  ): Promise<
    Array<{
      receipt: SaleReceipt
      fullyReturned: boolean
      partiallyReturned: boolean
      byLine: number[]
      /**
       * @deprecated Оставлено для совместимости UI. С 65.10 нефискальная
       * продажа возвращается обычным нефискальным возвратом, а не коррекцией.
       */
      requiresCorrection: boolean
      /** Исходная продажа без фискального чека: возврат наследует non_fiscal. */
      nonFiscalSource: boolean
    }>
  > {
    // Окно 7 дней: чек из прошлой смены должен находиться и на бойком терминале.
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000
    const recent = await this.db.receipts
      .where('createdAt')
      .aboveOrEqual(since)
      .reverse()
      .limit(500)
      .toArray()
    const sales = recent.filter(
      (r) =>
        (r.docType ?? 'sale') === 'sale' &&
        (r.settlementType ?? 'income') !== 'income_return' &&
        // 65.10: held/отменённые/недофискализированные чеки не предлагаем.
        isReturnSourceAllowed(r.status)
    )
    const q = query.trim().toLowerCase()
    const matched = q
      ? sales.filter((r) => {
          const d = new Date(r.createdAt)
          const dateRu = d.toLocaleDateString('ru-RU')
          const dateIso = d.toISOString().slice(0, 10)
          return (
            r.receiptId.toLowerCase().includes(q) ||
            dateRu.includes(q) ||
            dateIso.includes(q) ||
            String(r.total) === q ||
            String(r.total).startsWith(q)
          )
        })
      : sales

    const result: Array<{
      receipt: SaleReceipt
      fullyReturned: boolean
      partiallyReturned: boolean
      byLine: number[]
      requiresCorrection: boolean
      nonFiscalSource: boolean
    }> = []
    for (const receipt of matched.slice(0, limit)) {
      const summary = await this.getReturnedSummary(receipt)
      result.push({
        receipt,
        ...summary,
        // С 65.10 нефискальный возврат — обычный документ с наследованием режима.
        requiresCorrection: false,
        nonFiscalSource: receipt.status === 'non_fiscal'
      })
    }
    return result
  }

  /** B3/65.14: задачи замены non_fiscal на fiscal (для уведомления кассиру). */
  async listReplacementTasks(limit = 50): Promise<ReplacementTask[]> {
    // Зависшие processing не должны навсегда блокировать смену.
    await this.recoverInterruptedOperations()
    const rows = await this.db.receipts
      .where('status')
      .equals('non_fiscal')
      .toArray()
    const now = Date.now()
    return rows
      .filter(
        (r) =>
          !r.trash &&
          (r.replacementState === 'awaiting_verification' ||
            r.replacementState === 'awaiting_print' ||
            r.replacementState === 'processing' ||
            r.replacementState === 'failed')
      )
      .sort((a, b) => a.createdAt - b.createdAt)
      .slice(0, limit)
      .map((receipt) => ({ receipt, ageMs: Math.max(0, now - receipt.createdAt) }))
  }

  /** B3/65.14: кассир отказался заменять чек (non_fiscal остаётся). */
  async cancelReplacement(
    receiptId: string,
    reason: string,
    cashierName?: string
  ): Promise<void> {
    await this.recoverInterruptedOperations()
    const current = await this.db.receipts
      .where('receiptId')
      .equals(receiptId)
      .first()
    if (!current || current.id == null) throw new Error('Чек замены не найден')
    if (current.status !== 'non_fiscal') {
      throw new Error('Заменять можно только нефискальный чек')
    }
    const shift = await this.assertActiveShiftFor(current)
    if (!shift.ok) throw new Error(shift.reason)

    await this.db.transaction('rw', this.db.receipts, async () => {
      const receipt = await this.db.receipts
        .where('receiptId')
        .equals(receiptId)
        .first()
      if (!receipt || receipt.id == null) throw new Error('Чек замены не найден')
      if (receipt.status !== 'non_fiscal') {
        throw new Error('Заменять можно только нефискальный чек')
      }
      if (receipt.replacementState === 'processing') {
        throw new Error('Замена уже выполняется — дождитесь завершения')
      }
      if (receipt.replacementError?.includes('Фискальный чек пробит')) {
        throw new Error(
          'Фискальный чек уже пробит — отмена запрещена. Повторите сохранение замены.'
        )
      }
      if (receipt.replacementState === 'cancelled') return
      await this.db.receipts.update(receipt.id, {
        replacementState: 'cancelled',
        replacementError: reason.trim() || 'Отменено кассиром',
        voidReason: 'replacement_cancelled',
        voidedBy: cashierName,
        markingPrintState: 'cancelled',
        replacementStartedAt: undefined
      })
    })
    await this.markQueuedForSync(receiptId)
  }

  /** 65.14: hold-чеки, готовые к фискализации/отмене (для уведомления). */
  async listHeldReceipts(limit = 50): Promise<ReplacementTask[]> {
    await this.recoverInterruptedOperations()
    const rows = await this.db.receipts
      .where('status')
      .equals('marking_review')
      .toArray()
    const now = Date.now()
    return rows
      .filter((r) => !r.trash)
      .sort((a, b) => a.createdAt - b.createdAt)
      .slice(0, limit)
      .map((receipt) => ({ receipt, ageMs: Math.max(0, now - receipt.createdAt) }))
  }

  /** 65.14: пробить фискальный чек по hold-чеку (деньги уже в ящике). */
  async printHeldReceipt(
    receiptId: string,
    options: PrintReplacementOptions
  ): Promise<PrintReplacementResult> {
    if (!fiscalService.getStatus().isConnected) {
      return { ok: false, reason: 'ККТ не подключена — чек нельзя пробить сейчас' }
    }

    // meta/shifts нужны внутри транзакции для проверки активной смены.
    const claimed = await this.db.transaction(
      'rw',
      [this.db.receipts, this.db.meta, this.db.shifts],
      async () => {
      const held = await this.db.receipts
        .where('receiptId')
        .equals(receiptId)
        .first()
      if (!held || held.id == null) return { ok: false as const, reason: 'Hold-чек не найден' }
      if (
        isNativeKkt() &&
        held.items.some((item) => Boolean(item.datamatrix)) &&
        !fiscalService.getMarkingReadiness().ok
      ) {
        return {
          ok: false as const,
          reason: `Маркированный hold-чек заблокирован: ${
            fiscalService.getMarkingReadiness().reason ?? 'ККТ не готова'
          }`,
          receipt: held
        }
      }
      if (held.status !== 'marking_review') {
        return { ok: false as const, reason: 'Чек уже не в hold', receipt: held }
      }
      const shift = await this.assertActiveShiftFor(held)
      if (!shift.ok) return { ok: false as const, reason: shift.reason, receipt: held }
      // Повтор разрешён после сбоя печати только при подтверждённом valid:
      // агент ККТ идемпотентен по receiptId, второй документ не напечатает.
      const printable =
        held.markingPrintState === 'ready' ||
        (held.markingPrintState === 'failed' && held.czStatus === 'valid')
      if (!printable) {
        return {
          ok: false as const,
          reason: 'Код ещё не подтверждён (нужен valid)',
          receipt: held
        }
      }
      if ((held.paymentMethod ?? 'cash') !== 'cash') {
        return {
          ok: false as const,
          reason: 'Печать hold-чека доступна только для наличных',
          receipt: held
        }
      }
      if (
        !Number.isFinite(options.confirmedAmount) ||
        Math.abs(options.confirmedAmount - held.total) > 0.01
      ) {
        return {
          ok: false as const,
          reason: `Сумма не совпадает: в чеке ${held.total.toFixed(2)}`,
          receipt: held
        }
      }
      const startedAt = held.markingPrintStartedAt ?? 0
      if (startedAt > Date.now() - MARKING_OPERATION_STALE_MS) {
        return {
          ok: false as const,
          reason: 'Печать уже выполняется — дождитесь ответа ККТ',
          receipt: held
        }
      }
      await this.db.receipts.update(held.id, {
        markingPrintStartedAt: Date.now()
      })
      return { ok: true as const, held }
      }
    )
    if (!claimed.ok) return claimed

    const held = claimed.held
    try {
      const result = await fiscalService.printFiscalReceipt({
        id: held.receiptId,
        cashierName: options.cashierName,
        total_amount: Math.abs(held.total),
        paymentMethod: 'cash',
        items: held.items.map((item) => ({
          name: item.name,
          price: item.price,
          quantity: item.qty,
          marking_code: item.datamatrix
        })),
        docType: 'sale',
        settlementType: 'income'
      })
      const fiscalData: FiscalReceiptData = {
        fiscalDocumentNumber: result.fiscalDocumentNumber,
        fiscalDocumentSign: result.fiscalDocumentSign,
        shiftNumber: result.shiftNumber,
        fiscalDriveNumber: result.fiscalDriveNumber,
        receiptNumber: result.receiptNumber,
        dateTime: result.dateTime
      }
      const printedAt = Date.now()
      const printed: SaleReceipt = {
        ...held,
        status: 'fiscalized',
        fiscalData,
        markingPrintState: 'printed',
        markingPrintStartedAt: undefined,
        markingComment: options.decisionComment,
        markingDecision: 'approve',
        markingDecisionSource: options.decisionSource ?? 'pos',
        markingDecidedBy: options.decidedBy ?? options.cashierName,
        markingDecidedAt: options.decidedAt ?? printedAt,
        markingAppliedAt: printedAt,
        markingApplyError: undefined
      }
      await this.db.receipts.put(printed)
      // Синк — вторичен: даже если очередь недоступна, фискальный чек уже есть.
      try {
        await this.markQueuedForSync(held.receiptId)
      } catch (queueError) {
        console.warn('[Sale] hold пробит, но не обновлена очередь синка:', queueError)
      }
      return { ok: true, receipt: printed, replaced: held }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      try {
        await this.db.receipts.update(held.id as number, {
          markingPrintState: 'failed',
          markingPrintStartedAt: undefined,
          markingComment: message
        })
        await this.markQueuedForSync(held.receiptId)
      } catch (persistError) {
        console.error('[Sale] не удалось сохранить ошибку печати hold:', persistError)
      }
      return { ok: false, reason: message, receipt: held }
    }
  }

  /** 65.14: отменить hold-чек (наличные): вернуть товар на склад, деньги — из ящика. */
  async cancelHeldReceipt(
    receiptId: string,
    reason: string,
    cashierName?: string,
    options: CancelHeldOptions = {}
  ): Promise<SaleReceipt> {
    await this.recoverInterruptedOperations()
    const current = await this.db.receipts
      .where('receiptId')
      .equals(receiptId)
      .first()
    if (!current || current.id == null) throw new Error('Hold-чек не найден')
    if (current.status !== 'marking_review') throw new Error('Чек уже не в hold')
    const shift = await this.assertActiveShiftFor(current)
    if (!shift.ok) throw new Error(shift.reason)

    const at = Date.now()
    const cancelMode = options.cancelMode ?? 'refund'
    let cancelled: SaleReceipt | null = null
    let writeoffDoc: Writeoff | null = null
    await this.db.transaction(
      'rw',
      [this.db.receipts, this.db.stock_movements, this.db.products, this.db.writeoffs],
      async (tx) => {
        const held = await this.db.receipts
          .where('receiptId')
          .equals(receiptId)
          .first()
        if (!held || held.id == null) throw new Error('Hold-чек не найден')
        if (held.status !== 'marking_review') throw new Error('Чек уже не в hold')
        if ((held.paymentMethod ?? 'cash') !== 'cash') {
          throw new Error('Отмена hold-чека доступна только для наличных')
        }
        const startedAt = held.markingPrintStartedAt ?? 0
        if (startedAt > at - MARKING_OPERATION_STALE_MS) {
          throw new Error('Печать уже выполняется — дождитесь ответа ККТ')
        }
        await this.stock.applyReceiptItems(
          held.items,
          { type: 'return', receiptId: held.receiptId, zoneId: held.zoneId, userId: cashierName },
          tx
        )
        if (cancelMode === 'writeoff') {
          writeoffDoc = await this.stock.createWriteoff(
            {
              receiptId: held.receiptId,
              items: held.items.map((item) => ({
                lineId: item.lineId,
                barcode: item.barcode,
                name: item.name,
                qty: item.qty,
                price: item.price,
                datamatrix: item.datamatrix
              })),
              reasonCode: options.writeoffReason ?? 'other',
              origin: 'marking_hold',
              comment: options.decisionComment ?? reason.trim() ?? 'Списание hold-чека',
              zoneId: held.zoneId,
              userId: cashierName,
              userName: cashierName
            },
            tx
          )
        }
        const patch: Partial<SaleReceipt> = {
          status: 'cancelled',
          markingPrintState: 'cancelled',
          markingPrintStartedAt: undefined,
          markingDecision: 'cancel',
          markingDecisionSource: options.decisionSource ?? 'pos',
          markingDecidedBy: options.decidedBy ?? cashierName,
          markingDecidedAt: options.decidedAt ?? at,
          markingAppliedAt: at,
          markingComment:
            options.decisionComment ?? (reason.trim() || 'Отменено на кассе'),
          markingCancelMode: cancelMode,
          markingApplyError: undefined,
          voidReason: 'marking_hold_cancelled'
        }
        await this.db.receipts.update(held.id as number, patch)
        cancelled = { ...held, ...patch }
      }
    )
    try {
      await this.markQueuedForSync(receiptId)
    } catch (queueError) {
      console.warn('[Sale] hold отменён, но не обновлена очередь синка:', queueError)
    }
    if (writeoffDoc) {
      void this.bus
        .emit(PosEvent.WRITEOFF_COMPLETED, writeoffDoc)
        .catch((error) => console.warn('[sale] WRITEOFF_COMPLETED:', error))
    }
    return cancelled ?? { ...current, status: 'cancelled', markingPrintState: 'cancelled' }
  }

  /** Пометить чек в очереди синка как неотправленный (после изменения статуса). */
  private async markQueuedForSync(receiptId: string, synced = false): Promise<void> {
    const queued = await this.db.sales_queue.get(receiptId)
    if (queued) {
      await this.db.sales_queue.update(receiptId, {
        is_synced: synced,
        sync_error: undefined,
        sync_rejected_at: undefined
      })
    }
  }

  /**
   * B3/65.14: заменить non_fiscal на фискальный.
   *
   * Деньги двигаются только здесь, после подтверждения кассира: возврат
   * наличных и повторный приём (физически делает кассир), затем фискальный
   * чек теми же позициями. Склад повторно НЕ трогаем: старая продажа уже
   * списала товар, новый чек — замена того же расчёта.
   */
  async printReplacement(
    receiptId: string,
    options: PrintReplacementOptions
  ): Promise<PrintReplacementResult> {
    await this.recoverInterruptedOperations()
    if (!fiscalService.getStatus().isConnected) {
      return { ok: false, reason: 'ККТ не подключена — замена требует фискализации сейчас' }
    }

    const before = await this.db.receipts
      .where('receiptId')
      .equals(receiptId)
      .first()
    if (!before || before.id == null) return { ok: false, reason: 'Чек замены не найден' }
    if (
      isNativeKkt() &&
      before.items.some((item) => Boolean(item.datamatrix)) &&
      !fiscalService.getMarkingReadiness().ok
    ) {
      return {
        ok: false,
        reason: `Маркированный чек заблокирован: ${
          fiscalService.getMarkingReadiness().reason ?? 'ККТ не готова'
        }`,
        receipt: before
      }
    }
    const shift = await this.assertActiveShiftFor(before)
    if (!shift.ok) return { ok: false, reason: shift.reason, receipt: before }

    const claimed = await this.db.transaction('rw', this.db.receipts, async () => {
      const old = await this.db.receipts
        .where('receiptId')
        .equals(receiptId)
        .first()
      if (!old || old.id == null) return { ok: false as const, reason: 'Чек замены не найден' }
      if (old.status !== 'non_fiscal') {
        return {
          ok: false as const,
          reason: 'Заменять можно только нефискальный чек',
          receipt: old
        }
      }
      if (old.trash || old.replacementState === 'done') {
        return { ok: false as const, reason: 'Чек уже заменён', receipt: old }
      }
      if (old.replacementState === 'processing') {
        const startedAt = old.replacementStartedAt ?? 0
        if (startedAt > Date.now() - MARKING_OPERATION_STALE_MS) {
          return {
            ok: false as const,
            reason: 'Замена уже выполняется — дождитесь ответа ККТ',
            receipt: old
          }
        }
      } else if (
        old.replacementState !== 'awaiting_print' &&
        old.replacementState !== 'failed'
      ) {
        return {
          ok: false as const,
          reason: 'Код ещё не подтверждён ЧЗ: дождитесь проверки',
          receipt: old
        }
      }
      if (old.czStatus !== 'valid') {
        return {
          ok: false as const,
          reason: 'Код не подтверждён как valid — фискальная продажа невозможна',
          receipt: old
        }
      }
      if ((old.paymentMethod ?? 'cash') !== 'cash') {
        return {
          ok: false as const,
          reason: 'Замена доступна только для наличных: карта/СБП пока не поддерживаются',
          receipt: old
        }
      }
      if (
        !Number.isFinite(options.confirmedAmount) ||
        Math.abs(options.confirmedAmount - old.total) > 0.01
      ) {
        return {
          ok: false as const,
          reason: `Сумма не совпадает: в чеке ${old.total.toFixed(2)}`,
          receipt: old
        }
      }

      // Стабильный id нового чека: при ретрае после сбоя ККТ/БД используем
      // тот же uuid, чтобы агент ККТ не напечатал второй документ.
      const newReceiptId = old.replacementReceiptId ?? this.newReceiptId()
      const createdAt = old.replacementConfirmedAt ?? Date.now()
      let draft = await this.db.receipts
        .where('receiptId')
        .equals(newReceiptId)
        .first()
      if (draft && draft.status !== 'draft') {
        return {
          ok: false as const,
          reason: 'Новый фискальный чек уже создан — завершите операцию',
          receipt: old
        }
      }
      if (!draft) {
        draft = {
          receiptId: newReceiptId,
          items: old.items.map((item) => ({ ...item })),
          total: old.total,
          status: 'draft',
          paymentMethod: 'cash',
          zoneId: shift.zoneId,
          shiftNumber: shift.shiftNumber,
          cashierName: options.cashierName,
          docType: 'sale',
          settlementType: 'income',
          replacesReceiptId: old.receiptId,
          czStatus: 'valid',
          createdAt
        }
        draft.id = (await this.db.receipts.add(draft)) as number
      }
      await this.db.receipts.update(old.id, {
        replacementState: 'processing',
        replacementReceiptId: newReceiptId,
        replacementStartedAt: Date.now(),
        replacementAttempts: (old.replacementAttempts ?? 0) + 1,
        replacementConfirmedAt: createdAt,
        replacementError: undefined,
        voidedBy: options.cashierName
      })
      return { ok: true as const, old, draft, newReceiptId, createdAt }
    })
    if (!claimed.ok) return claimed

    return this.finishReplacement(
      claimed.old,
      claimed.draft,
      claimed.draft.id as number,
      claimed.newReceiptId,
      claimed.createdAt
    )
  }

  /** Завершить замену: ККТ, tombstone старого чека, синк. */
  private async finishReplacement(
    old: SaleReceipt,
    draft: SaleReceipt,
    draftDbId: number,
    newReceiptId: string,
    createdAt: number
  ): Promise<PrintReplacementResult> {
    let printed = false
    try {
      const deviceId = await this.getDeviceId()
      const result = await fiscalService.printFiscalReceipt({
        id: newReceiptId,
        cashierName: draft.cashierName,
        total_amount: Math.abs(old.total),
        paymentMethod: 'cash',
        items: old.items.map((item) => ({
          name: item.name,
          price: item.price,
          quantity: item.qty,
          marking_code: item.datamatrix
        })),
        docType: 'sale',
        settlementType: 'income'
      })
      printed = true
      const fiscalData: FiscalReceiptData = {
        fiscalDocumentNumber: result.fiscalDocumentNumber,
        fiscalDocumentSign: result.fiscalDocumentSign,
        shiftNumber: result.shiftNumber,
        fiscalDriveNumber: result.fiscalDriveNumber,
        receiptNumber: result.receiptNumber,
        dateTime: result.dateTime
      }
      const completedAt = Date.now()
      const newReceipt: SaleReceipt = {
        ...draft,
        id: draftDbId,
        status: 'fiscalized',
        fiscalData
      }
      const replaced: SaleReceipt = {
        ...old,
        status: 'voided',
        trash: true,
        replacementState: 'done',
        replacedByReceiptId: newReceiptId,
        voidedAt: completedAt,
        voidReason: 'replaced_by_fiscal',
        voidedBy: draft.cashierName,
        replacementCompletedAt: completedAt,
        replacementError: undefined,
        replacementStartedAt: undefined,
        // У старого non_fiscal не было фискальной печати: не выдаём это за неё.
        markingPrintState: undefined
      }
      const replacementItems: CheckoutItem[] = old.items.map((item) => ({
        lineId: item.lineId,
        barcode: item.barcode,
        gtin: item.gtin,
        name: item.name,
        qty: item.qty,
        price: item.price,
        unit: item.unit,
        datamatrix: item.datamatrix,
        requiresMarking: item.requiresMarking,
        markingSkipped: item.markingSkipped,
        markingInvalid: item.markingInvalid,
        markingReason: item.markingReason,
        czStatus: 'valid' as const
      }))
      // 65.14: новый фискальный чек и его запись в sales_queue фиксируем
      // ОДНОЙ транзакцией. Иначе при сбое очереди fiscal-чек остался бы только
      // в ККТ/локально и не дошёл до облака.
      await this.db.transaction('rw', [this.db.receipts, this.db.sales_queue], async () => {
        await this.db.receipts.put(newReceipt)
        await this.db.receipts.put(replaced)
        await this.db.sales_queue.add(
          this.buildSyncQueueRow(
            deviceId,
            newReceiptId,
            replacementItems,
            old.total,
            new Date(createdAt).toISOString(),
            'cash',
            fiscalData,
            'fiscalized',
            {
              zoneId: newReceipt.zoneId,
              shiftNumber: newReceipt.shiftNumber,
              cashierName: draft.cashierName
            },
            'income',
            old.receiptId
          )
        )
        await this.markQueuedForSync(old.receiptId)
      })
      return { ok: true, receipt: newReceipt, replaced }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      // Печать уже состоялась — откатывать операцию в failed нельзя:
      // фискальный документ существует. Оставляем processing, чтобы recovery
      // вернул задачу в failed, а повтор использовал тот же uuid.
      if (printed) {
        try {
          await this.db.receipts.update(old.id as number, {
            replacementState: 'processing',
            replacementStartedAt: undefined,
            replacementError:
              `Фискальный чек пробит, но не сохранён: ${message}. ` +
              'Повторите попытку — повторная печать по тому же uuid безопасна.'
          })
        } catch (persistError) {
          console.error('[Sale] замена пробита, но состояние не сохранено:', persistError)
        }
        return {
          ok: false,
          reason: `Фискальный чек пробит, но не сохранён: ${message}`,
          receipt: old
        }
      }

      // До ККТ не дошли: черновик оставляем для повторного использования
      // стабильного replacementReceiptId (второй фискальный документ не появится).
      try {
        await this.db.receipts.update(old.id as number, {
          replacementState: 'failed',
          replacementStartedAt: undefined,
          replacementError: message
        })
        await this.markQueuedForSync(old.receiptId)
      } catch (persistError) {
        console.error('[Sale] не удалось сохранить ошибку замены:', persistError)
      }
      return { ok: false, reason: message, receipt: old }
    }
  }

  async checkout(input: CheckoutInput, options: CheckoutOptions = {}): Promise<CheckoutResult> {
    const items = input.items
    if (items.length === 0) {
      throw new Error('Чек пуст')
    }
    // Модуль маркировки может быть полностью выключен директором. В этом
    // режиме ядро не применяет marking-правила, но фиксирует причину в чеке.
    const markingEnabled = this.marking?.isEnabled() ?? true;
    if (!markingEnabled) {
      for (const item of items) {
        item.requiresMarking = false;
        item.markingSkipped = true;
        item.markingReason = 'Модуль маркировки отключён';
      }
    }
    // 65.10: не доверяем UI-флагу `requiresMarking` — если товар в каталоге
    // помечен как маркируемый, правило применяется даже при `false` от кассы.
    for (const item of items) {
      if (!item.barcode || !markingEnabled) continue
      const product = await this.db.products.where('barcode').equals(item.barcode).first()
      if (product?.requiresMarking) item.requiresMarking = true
    }
    const receiptId = this.newReceiptId()
    const isReturn = input.settlementType === 'income_return'

    // 65.12.1: структура кода маркировки проверяется локально и ДО плагинов.
    // Регистр и разделитель GS сохраняются: приведение к другому регистру
    // ломает код для ЧЗ и тега 2000.
    for (const item of items) {
      if (!item.datamatrix) continue
      // Ядро не знает формат кода: спрашиваем модуль юрисдикции.
      if (!this.marking) continue
      const inspection = this.marking.inspect(item.datamatrix)
      if (!inspection.acceptable) {
        return {
          ok: false,
          stage: 'validation',
          reason: `«${item.name}»: ${inspection.reason ?? 'код недействителен'}`,
          receiptId
        }
      }
      item.datamatrix = inspection.normalized
    }
    // Фискальное НАМЕРЕНИЕ кассира (галочка), не зависит от доступности ККТ.
    let fiscalIntent = options.fiscalIntent ?? true
    // B3/65.14: non_fiscal-чек с непроверенными кодами, который после
    // проверки должен быть заменён фискальным (возврат наличных + продажа).
    let replacementPending = false

    // Возврат хранится отрицательной суммой: SUM в отчётах сразу даёт нетто.
    const amount = items.reduce((sum, i) => sum + i.price * i.qty, 0)
    const total = isReturn ? -amount : amount

    // ARCH-TASK-65.5: сверка возврата с исходным чеком ДО фискализации.
    // Лимит — накопительный по всем возвратам, уже связанным с этим чеком.
    const originalReceiptId = isReturn ? input.originalReceiptId?.trim() || undefined : undefined
    if (isReturn && originalReceiptId) {
      const source = await this.db.receipts
        .where('receiptId')
        .equals(originalReceiptId)
        .first()
      if (!source) {
        return {
          ok: false,
          stage: 'validation',
          reason: 'Исходный чек не найден на этой кассе',
          receiptId
        }
      }
      const sourceIsSale =
        (source.docType ?? 'sale') === 'sale' &&
        (source.settlementType ?? 'income') !== 'income_return'
      if (!sourceIsSale) {
        return {
          ok: false,
          stage: 'validation',
          reason: 'Возврат можно оформить только по чеку продажи',
          receiptId
        }
      }
      // 65.10: held/отменённый/черновик — возвращать нельзя, пока чек не решён.
      if (!isReturnSourceAllowed(source.status)) {
        return {
          ok: false,
          stage: 'validation',
          reason:
            source.status === 'marking_review'
              ? 'Чек ждёт решения по маркировке — возврат пока невозможен'
              : source.status === 'cancelled'
                ? 'Чек отменён — возврат по нему невозможен'
                : 'Возврат по этому чеку невозможен',
          receiptId
        }
      }
      // Line-level: каждая возвращаемая строка ссылается на строку исходного
      // чека. Для маркированного берём её код и запрещаем qty > 1.
      for (const item of items) {
        if (!item.originalLineId) continue
        const sourceItem = source.items.find((s) => s.lineId === item.originalLineId)
        if (!sourceItem) {
          return {
            ok: false,
            stage: 'validation',
            reason: 'Возвращаемая строка не найдена в исходном чеке',
            receiptId
          }
        }
        if (sourceItem.datamatrix) {
          if (item.qty > 1 + 1e-3) {
            return {
              ok: false,
              stage: 'validation',
              reason: 'Маркированная позиция возвращается по одной единице',
              receiptId
            }
          }
          // Код возврата — ровно тот, который был продан (возврат в оборот ЧЗ).
          // 65.12.1: исторические коды не перепроверяем структурно — иначе
          // старые чеки с «мусорной» маркой нельзя было бы вернуть. Новые коды
          // валидируются при продаже (см. блок выше).
          item.datamatrix = sourceItem.datamatrix
          item.requiresMarking = true
        } else if (item.requiresMarking && source.status !== 'non_fiscal') {
          // Фискальная продажа маркированного без кода невозможна: нечего
          // возвращать в оборот.
          return {
            ok: false,
            stage: 'validation',
            reason: 'В исходном чеке нет кода маркировки для этой строки',
            receiptId
          }
        }
      }
      const alreadyReturned = await this.returnedItemsOf(originalReceiptId)
      // Для сверки requested.lineId = строка исходного чека (originalLineId).
      // Если исходный чек старый (без lineId) — не подставляем id новой строки,
      // иначе ключ не совпадёт: fallback пойдёт по DataMatrix/штрихкоду.
      const requested = items.map((i) => ({
        ...i,
        lineId: i.originalLineId
      }))
      const check = validateReturnItems(source.items, alreadyReturned, requested)
      if (!check.ok) {
        return { ok: false, stage: 'validation', reason: check.reason, receiptId }
      }

      // 65.10: возврат наследует режим исходника. Нефискальная продажа →
      // нефискальный возврат (без ККТ и очереди); фискальная → фискальный.
      const inherited = resolveReturnFiscalIntent(source)
      fiscalIntent = inherited === 'fiscal'
      if (!fiscalIntent && input.paymentMethod && input.paymentMethod !== 'cash') {
        return {
          ok: false,
          stage: 'validation',
          reason: 'Нефискальный возврат возможен только наличными',
          receiptId
        }
      }
    } else if (isReturn) {
      // Возврат без исходного чека: режим задаёт кассир (по умолчанию фискальный).
      // 65.10: маркированную строку без кода нельзя вернуть «молча».
      const manualMarked = items.filter((i) => i.requiresMarking)
      const skippedReturnMarked = manualMarked.filter(
        (i) => i.markingSkipped || i.markingInvalid
      )
      if (skippedReturnMarked.length > 0) {
        if (!options.canSkipMarking) {
          return {
            ok: false,
            stage: 'validation',
            reason: 'Нет права на нефискальный возврат маркированного товара',
            receiptId
          }
        }
        if (skippedReturnMarked.some((i) => !i.markingReason?.trim())) {
          return {
            ok: false,
            stage: 'validation',
            reason: 'Укажите причину возврата маркированного товара без кода',
            receiptId
          }
        }
        fiscalIntent = false
      }
      const missingReturnMarked = manualMarked.filter(
        (i) => !i.datamatrix && !i.markingSkipped && !i.markingInvalid
      )
      if (missingReturnMarked.length > 0) {
        return {
          ok: false,
          stage: 'validation',
          reason: 'Укажите код маркировки или оформите нефискальный возврат по праву',
          receiptId
        }
      }
      if (!fiscalIntent && input.paymentMethod && input.paymentMethod !== 'cash') {
        return {
          ok: false,
          stage: 'validation',
          reason: 'Нефискальный возврат возможен только наличными',
          receiptId
        }
      }
    } else if (input.paymentMethod && input.paymentMethod !== 'cash' && !fiscalIntent) {
      // Защита в глубину: карта и СБП фискализируются ВСЕГДА. Нефискальный
      // чек по безналу невозможен, даже если UI/вызов передал fiscalIntent=false.
      console.warn('[Sale] нефискальная продажа картой/СБП запрещена — форсируем фискализацию')
      fiscalIntent = true
    }

    // 65.10: списание — только вместе с возвратом и только по праву.
    if (!isReturn && input.writeoff) {
      return {
        ok: false,
        stage: 'validation',
        reason: 'Списание оформляется только вместе с возвратом',
        receiptId
      }
    }
    if (input.writeoff && !options.canWriteoff) {
      return {
        ok: false,
        stage: 'validation',
        reason: 'Нет права оформлять списание',
        receiptId
      }
    }

    const saleContext: SaleContext = {
      receiptId,
      items: this.toSaleItems(items),
      total,
      settlementType: input.settlementType ?? 'income'
    }

    // 1. PRE_SALE — плагины могут наложить veto (Честный Знак).
    //    Плагин ЧЗ может дописать коды DataMatrix в позиции saleContext.items —
    //    синхронизируем их обратно во входные items, чтобы коды попали в чек.
    //    Для возврата проверки ЧЗ не запускаем (возврат кодов в оборот — 65.x).
    if (!isReturn) {
      const preSale = await this.bus.emit(PosEvent.PRE_SALE, saleContext)
      if (!preSale.ok) {
        return {
          ok: false,
          stage: 'pre_sale',
          reason: preSale.vetoReason ?? 'Продажа заблокирована плагином',
          receiptId,
          markingIssues: preSale.markingIssues
        }
      }
      saleContext.items.forEach((si, idx) => {
        if (si.datamatrix && items[idx]) {
          items[idx].datamatrix = si.datamatrix
        }
      })

      // 65.10: правила маркировки для ПРОДАЖИ.
      // - валидный код → только фискальный чек (галочка игнорируется);
      // - invalid/пустой по праву → non_fiscal + только наличные;
      // - смешивать валидные и проблемные марки в одном нефискальном чеке нельзя.
      const marked = items.filter((i) => i.requiresMarking)
      const skipped = marked.filter((i) => i.markingSkipped || i.markingInvalid)
      // Только подтверждённый `valid` — валидная марка. `unknown`/нет статуса
      // больше не считаем валидной: иначе B3 (non_fiscal → замена) невозможен.
      const validMarked = marked.filter(
        (i) =>
          i.datamatrix &&
          i.czStatus === 'valid' &&
          !i.markingSkipped &&
          !i.markingInvalid
      )
      const unknownMarked = marked.filter(
        (i) =>
          i.datamatrix &&
          (!i.czStatus || i.czStatus === 'unknown') &&
          !i.markingSkipped &&
          !i.markingInvalid
      )
      const missingMarked = marked.filter(
        (i) => !i.datamatrix && !i.markingSkipped && !i.markingInvalid
      )
      // Пустой код — всегда блокировка. Продать без кода можно только осознанно:
      // markingSkipped + право + причина (тогда позиция попадает в skipped, а не
      // в missingMarked). Это закрывает обход через `requiresMarking: false`.
      if (missingMarked.length > 0) {
        return {
          ok: false,
          stage: 'pre_sale',
          reason:
            'Маркированный товар без кода можно продать только по праву «Без кода» (наличные)',
          receiptId,
          markingIssues: missingMarked.map((i) => ({
            lineId: i.lineId,
            name: i.name,
            kind: 'missing' as const,
            reason: 'Код маркировки не введён'
          }))
        }
      }
      if (skipped.length > 0) {
        if (!options.canSkipMarking) {
          return {
            ok: false,
            stage: 'pre_sale',
            reason: 'Нет права продавать маркированный товар без кода',
            receiptId
          }
        }
        if (skipped.some((i) => !i.markingReason?.trim())) {
          return {
            ok: false,
            stage: 'pre_sale',
            reason: 'Укажите причину продажи маркированного товара без кода',
            receiptId
          }
        }
        if (validMarked.length > 0) {
          return {
            ok: false,
            stage: 'pre_sale',
            reason:
              'Уберите из нефискального чека товары с валидной маркировкой — их коды останутся в обороте',
            receiptId
          }
        }
        if (unknownMarked.length > 0) {
          return {
            ok: false,
            stage: 'pre_sale',
            reason:
              'В чеке есть непроверенные коды: дождитесь проверки ЧЗ или уберите эти позиции',
            receiptId
          }
        }
        fiscalIntent = false
        if (input.paymentMethod && input.paymentMethod !== 'cash') {
          return {
            ok: false,
            stage: 'pre_sale',
            reason: 'Продажа маркированного товара без кода возможна только наличными',
            receiptId
          }
        }
      } else if (validMarked.length > 0) {
        // Валидная марка не может быть продана нефискально.
        fiscalIntent = true
      } else if (unknownMarked.length > 0 && !fiscalIntent) {
        // B3/65.14: непроверенный код можно продать нефискально ТОЛЬКО
        // наличными. После проверки `valid` касса предложит замену на
        // фискальный чек (возврат наличных + повторная продажа).
        if (!options.canSkipFiscalization) {
          return {
            ok: false,
            stage: 'pre_sale',
            reason: 'Нет права снять фискализацию с чека',
            receiptId
          }
        }
        if (input.paymentMethod && input.paymentMethod !== 'cash') {
          return {
            ok: false,
            stage: 'pre_sale',
            reason:
              'Непроверенный код можно продать нефискально только за наличные. ' +
              'Карта и СБП требуют фискализации: дождитесь проверки или уберите позицию',
            receiptId
          }
        }
        replacementPending = true
      }

      // Снять фискализацию с обычной (немаркированной) продажи — по праву.
      if (!fiscalIntent && skipped.length === 0 && !options.canSkipFiscalization) {
        return {
          ok: false,
          stage: 'pre_sale',
          reason: 'Нет права снять фискализацию с чека',
          receiptId
        }
      }
    }

    // 65.14: известный `invalid` из True API блокирует оплату до того, как
    // деньги списаны (карта/СБП/наличные). Кассир убирает позицию или
    // оформляет «Без кода» (non_fiscal, только наличные).
    const apiInvalid = items.filter(
      (i) =>
        i.requiresMarking &&
        i.datamatrix &&
        i.czStatus === 'invalid' &&
        !i.markingInvalid &&
        !i.markingSkipped
    )
    if (!isReturn && apiInvalid.length > 0) {
      return {
        ok: false,
        stage: 'pre_sale',
        reason: options.canSkipMarking
          ? 'Код не прошёл проверку ЧЗ: уберите позицию или оформите «Без кода» (наличные)'
          : 'Код не прошёл проверку ЧЗ: продажа запрещена, уберите позицию',
        receiptId,
        markingIssues: apiInvalid.map((i) => ({
          lineId: i.lineId,
          name: i.name,
          code: i.datamatrix,
          kind: 'invalid' as const,
          reason: 'Код не найден в Честном ЗНАКе'
        }))
      }
    }

    // Native Android KKT допускает маркировку только после подтверждённого
    // preflight ФФД 1.2/ЕСМ/тега 2000. Проверка стоит после PRE_SALE, поэтому
    // ловит и позиции, которым плагин маркировки успел проставить requiresMarking.
    const markingReadiness = fiscalService.getMarkingReadiness()
    const nativeMarkingBlocked =
      options.nativeMarkingBlocked ?? (isNativeKkt() && !markingReadiness.ok)
    if (nativeMarkingBlocked && items.some((i) => i.requiresMarking)) {
      return {
        ok: false,
        stage: 'pre_sale',
        reason:
          `Маркированный чек заблокирован: ${markingReadiness.reason ?? 'ККТ не готова к ФФД 1.2'}.`,
        receiptId
      }
    }

    // 65.14: hold с непроверенным кодом и картой/СБП запрещён до реализации
    // возврата: иначе деньги списываются, а чек «висит». Только наличные.
    const unknownHold =
      !isReturn &&
      fiscalIntent &&
      items.some(
        (i) =>
          i.requiresMarking &&
          i.datamatrix &&
          (!i.czStatus || i.czStatus === 'unknown') &&
          !i.markingSkipped &&
          !i.markingInvalid
      )
    if (unknownHold && (input.paymentMethod ?? 'cash') !== 'cash') {
      return {
        ok: false,
        stage: 'pre_sale',
        reason:
          'Код ещё не проверен: оплата картой/СБП временно недоступна. ' +
          'Дождитесь проверки или используйте наличные',
        receiptId
      }
    }

    // 2. PAYMENT_REQUESTED — плагин оплаты (СБП NewPay срабатывает только для 'sbp').
    //    При возврате деньги отдаём сами, эквайринг не вызываем.
    if (!isReturn) {
      const payment = await this.bus.emit(PosEvent.PAYMENT_REQUESTED, {
        receiptId,
        amount: total,
        method: input.paymentMethod ?? 'cash'
      })
      if (!payment.ok) {
        return {
          ok: false,
          stage: 'payment',
          reason: payment.vetoReason ?? 'Оплата не прошла',
          receiptId
        }
      }
    }

    // 3. Фискализация (ФЗ-54, АТОЛ ДТО 10) — ТРИ исхода по fiscalIntent и ККТ.
    //    Оплата уже прошла (деньги взяты), поэтому чек сохраняется ВСЕГДА, а
    //    статус фиксирует его фискальную судьбу. Недоступность ККТ больше НЕ
    //    блокирует продажу — чек уходит в очередь дофискализации.
    let fiscalData: FiscalReceiptData | undefined
    let status: ReceiptStatus
    let warning: string | undefined
    const createdAt = Date.now()
    const fiscalItems = items.map((i) => ({
      name: i.name,
      price: i.price,
      quantity: i.qty,
      unit: i.unit,
      marking_code: i.datamatrix
    }))

    // 65.14: если хотя бы один маркированный код не проверен (`unknown` или
    // проверка не завершилась), чек уходит в hold: деньги приняты любым
    // способом, фискального чека нет, смена не закрывается до решения.
    const markedWithCode = items.filter(
      (i) => i.requiresMarking && i.datamatrix && !i.markingSkipped && !i.markingInvalid
    )
    const unknownMarked = markedWithCode.filter(
      (i) => !i.czStatus || i.czStatus === 'unknown'
    )
    const holdMarking = !isReturn && fiscalIntent && unknownMarked.length > 0

    if (holdMarking) {
      status = 'marking_review'
      warning =
        'Код маркировки не проверен: чек ждёт проверки/решения. Смена не закроется до решения.'
    } else if (!fiscalIntent) {
      // (1) Кассир умышленно отказался: НИКОГДА не фискализируем и НЕ в очередь.
      status = 'non_fiscal'
    } else if (fiscalService.getStatus().isConnected) {
      try {
        const result = await fiscalService.printFiscalReceipt({
          id: receiptId,
          cashierName: options.cashierName,
          // В ККТ сумма всегда положительная — направление задаёт признак расчёта.
          total_amount: Math.abs(total),
          paymentMethod: input.paymentMethod,
          items: fiscalItems,
          docType: 'sale',
          settlementType: input.settlementType ?? 'income'
        })
        fiscalData = {
          fiscalDocumentNumber: result.fiscalDocumentNumber,
          fiscalDocumentSign: result.fiscalDocumentSign,
          shiftNumber: result.shiftNumber,
          fiscalDriveNumber: result.fiscalDriveNumber,
          receiptNumber: result.receiptNumber,
          dateTime: result.dateTime
        }
        // (2) ККТ пробила чек.
        status = 'fiscalized'
        if (result.notPrinted) {
          warning = 'ККТ фискализировала чек, но не напечатала его: проверьте бумагу и допечатайте документ.'
        }
      } catch (err) {
        // (3a) ККТ подключена, но сбой печати (нет бумаги и т.п.) — откладываем.
        const reason = err instanceof Error ? err.message : String(err)
        status = 'offline_pending'
        warning = `ККТ не пробила чек (${reason}). Чек будет фискализирован позже.`
      }
    } else {
      // (3b) ККТ недоступна (блэкаут/агент не запущен) — откладываем дофискализацию.
      status = 'offline_pending'
      warning = 'ККТ недоступна — чек будет фискализирован автоматически позже.'
    }

    // 4. Записываем чек в Dexie (статус, фискальные реквизиты и КОНТЕКСТ СМЕНЫ).
    const shiftContext = await this.getActiveShiftContext()
    const deviceId = await this.getDeviceId()
    const offlineReceiptNumber =
      status === 'fiscalized'
        ? undefined
        : 'OFF-' + deviceId.slice(-8).toUpperCase() + '-' + receiptId.slice(0, 8).toUpperCase()
    const receipt: SaleReceipt = {
      receiptId,
      items: this.toReceiptItems(items, isReturn),
      total,
      status,
      paymentMethod: input.paymentMethod,
      // 65.10: чек без ККТ по маркировке (заполняется правилами маркировки).
      markingSkipped: items.some((i) => i.markingSkipped),
      markingInvalid: items.some((i) => i.markingInvalid),
      markingReason:
        items.find((i) => i.markingReason)?.markingReason ??
        (holdMarking ? 'Код маркировки не проверен: проверка отложена' : undefined),
      // 65.14: hold-чек проходит проверку; печатать можно только после valid.
      markingPrintState: holdMarking ? 'checking' : undefined,
      // B3: non_fiscal с непроверенными кодами ждёт проверки и замены.
      replacementState: replacementPending ? 'awaiting_verification' : undefined,
      fiscalData,
      docType: 'sale',
      settlementType: input.settlementType ?? 'income',
      originalReceiptId,
      zoneId: shiftContext.zoneId,
      storeId: shiftContext.storeId,
      shiftNumber: shiftContext.shiftNumber,
      offlineReceiptNumber,
      cashierName: options.cashierName,
      createdAt
    }
    // 65.10: все локальные записи — в ОДНОЙ транзакции. Если склад/списание
    // падают, чек и очередь синка тоже откатываются (не остаётся «продажа без
    // склада»). Печать ККТ — внешняя операция и в транзакцию не входит.
    const stockItems = this.toReceiptItems(items, isReturn)
    let saved: SaleReceipt | null = null
    let writeoffDoc: Writeoff | null = null
    await this.db.transaction(
      'rw',
      [
        this.db.receipts,
        this.db.sales_queue,
        this.db.offlineQueue,
        this.db.stock_movements,
        this.db.writeoffs,
        this.db.products,
        this.db.meta
      ],
      async (tx) => {
        const receiptIdInDb = (await this.db.receipts.add(receipt)) as number
        saved = { ...receipt, id: receiptIdInDb }

        // 5. Очередь синхронизации с облаком.
        await this.enqueueForSync(
          receiptId,
          items,
          total,
          new Date(createdAt).toISOString(),
          input.paymentMethod,
          fiscalData,
          status,
          { ...shiftContext, cashierName: options.cashierName },
          input.settlementType ?? 'income',
          originalReceiptId,
          offlineReceiptNumber
        )

        // 6. Очередь дофискализации — СТРОГО для offline_pending.
        if (status === 'offline_pending') {
          await this.db.offlineQueue.add({
            type: 'fiscalize',
            payload: {
              receiptId,
              total,
              // В очередь кладём уже подготовленные фискальные позиции:
              // `items` содержат `datamatrix`, а воркер и ККТ ждут `marking_code`.
              items: fiscalItems,
              docType: 'sale',
              settlementType: input.settlementType ?? 'income'
            },
            attempts: 0,
            status: 'pending',
            createdAt
          })
        }

        // 65.10: склад — только через ledger (stock_movements).
        await this.stock.applyReceiptItems(
          stockItems,
          {
            type: isReturn ? 'return' : 'sale',
            receiptId,
            zoneId: shiftContext.zoneId,
            userId: options.cashierName
          },
          tx
        )

        // «Возврат + списание»: возврат уже вернул товар на склад, теперь списываем.
        if (isReturn && input.writeoff) {
          const doc = await this.stock.createWriteoff(
            {
              receiptId,
              items: stockItems.map((i) => ({
                lineId: i.lineId,
                barcode: i.barcode,
                name: i.name,
                qty: i.qty,
                price: i.price,
                datamatrix: i.datamatrix
              })),
              reasonCode: input.writeoff.reasonCode,
              origin: 'return_writeoff',
              comment: input.writeoff.comment,
              zoneId: shiftContext.zoneId,
              userId: options.cashierName,
              userName: options.cashierName
            },
            tx
          )
          await this.db.receipts.update(receiptIdInDb, { writeoffId: doc.writeoffId })
          saved.writeoffId = doc.writeoffId
          writeoffDoc = doc
        }

        return receiptIdInDb
      }
    )

    if (!saved) {
      throw new Error('Чек не сохранён: транзакция не вернула запись')
    }

    if (writeoffDoc) {
      void this.bus
        .emit(PosEvent.WRITEOFF_COMPLETED, writeoffDoc)
        .catch((error) => console.warn('[sale] WRITEOFF_COMPLETED:', error))
    }

    // 7. SALE_COMPLETED — пост-обработка (Меркурий и т.п.). Ошибки пост-
    //    обработки не откатывают уже оплаченный чек, но логируются.
    const completed = await this.bus.emit(PosEvent.SALE_COMPLETED, saleContext)
    if (!completed.ok) {
      console.warn('[sale] SALE_COMPLETED пост-обработка с замечанием:', completed.vetoReason)
    }

    return { ok: true, receipt: saved, warning }
  }
}
